import { useEffect, useRef } from 'react';
import { eventsModule } from '@/events/events-module';
import { useHeaderStore, type HeaderPanel } from '@/header/header-store';
import { getTracker } from '@/telemetry/tracker';
import { AppsPanel } from './AppsPanel';
import { HelpPanel } from './HelpPanel';
import { UserPanel } from './UserPanel';
import { TeamsPanel } from './TeamsPanel';
import { ChatbotPanel } from './ChatbotPanel';

function panelTitle(panel: HeaderPanel): string {
  switch (panel) {
    case 'apps':
      return 'Apps';
    case 'help':
      return 'Help';
    case 'user':
      return 'My Account';
    case 'teams':
      return 'My Team';
    case 'chatbot':
      return 'EYQ FS';
  }
}

export function HeaderUtilityPanels() {
  const panel = useHeaderStore((s) => s.openPanel);
  const togglePanel = useHeaderStore((s) => s.togglePanel);
  const closePanel = useHeaderStore((s) => s.closePanel);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const offApps = eventsModule.on('shell:open-workspaces', () => {
      getTracker().event('Header-apps');
      togglePanel('apps');
    });
    const offHelp = eventsModule.on('shell:open-help', () => {
      getTracker().event('Header-support');
      togglePanel('help');
    });
    return () => {
      offApps();
      offHelp();
    };
  }, [togglePanel]);

  useEffect(() => {
    if (!panel) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // Don't close if clicking inside the panel itself
      if (panelRef.current && panelRef.current.contains(target)) return;
      // Don't close if clicking inside a Motif tooltip/portal (rendered outside the panel DOM)
      if (target.closest?.('motif-tooltip-content, motif-portal, [data-motif-portal]')) return;
      closePanel();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [panel, closePanel]);

  if (!panel) return null;

  // Chatbot panel gets its own full-height wrapper — it manages its own header
  if (panel === 'chatbot') {
    return (
      <aside
        className="header-utility-panel header-utility-panel--chatbot"
        ref={panelRef}
        aria-label={panelTitle(panel)}
      >
        <ChatbotPanel onClose={closePanel} />
      </aside>
    );
  }

  // User, teams, apps, and help panels render as dark, full-height slide-ins
  const isDarkPanel = panel === 'user' || panel === 'teams' || panel === 'apps' || panel === 'help';
  const className = isDarkPanel
    ? 'header-utility-panel header-utility-panel--user'
    : 'header-utility-panel';

  return (
    <aside className={className} ref={panelRef} aria-label={panelTitle(panel)}>
      <div className="header-utility-panel__header">
        <h2>{panelTitle(panel)}</h2>
        <button type="button" onClick={() => closePanel()} aria-label="Close panel">
          <span className="material-icons-outlined" aria-hidden="true">
            close
          </span>
        </button>
      </div>

      {panel === 'apps' ? <AppsPanel onClose={closePanel} /> : null}
      {panel === 'help' ? <HelpPanel /> : null}
      {panel === 'user' ? <UserPanel /> : null}
      {panel === 'teams' ? <TeamsPanel /> : null}
    </aside>
  );
}
